import {Children, Fragment, useState, useEffect} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

// Style
import {pad, border, radius, transition} from "../style/components/variables.js";
import {flex} from "../style/components/mixins.js";
import {triangle} from "../style/components/shapes.js";
import {voice} from "../style/components/typography.js";
import {Abbr, Label} from "../style/components/general.js";

const Accordion = ({
  testId = "accordion",
  children,
  label,
  handleOpen,
  toggleOpen = false,
  disabled = false,
  isGroup = false,
  wrap = false,
  arrowRight = false,
  compact = false
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(toggleOpen);
  }, [toggleOpen]);

  const labelText = value =>
    String(value)
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const renderLabelNode = node => {
    if (typeof node === "string" || typeof node === "number") {
      const text = labelText(node);
      if (!text) return null;
      return <Ellipsis title={text}>{text}</Ellipsis>;
    }
  
    if (node?.type === Fragment) return Children.map(node.props.children, renderLabelNode);
  
    return node;
  };

  const heading = (
    <Label
      as={typeof label === "string" ? "label" : "span"}
      isGroup={isGroup ? 1 : 0}
      bold
      wrap={wrap ? 1 : 0}>
      {typeof label === "string" ? <Abbr title={label}>{label}</Abbr> : renderLabelNode(label)}
    </Label>
  );

  const arrow = (
    <Arrow
      openDrawer={open}
      isGroup={isGroup ? 1 : 0}
      arrowRight={arrowRight ? 1 : 0}
      compact={compact ? 1 : 0}
    />
  );

  return (
    <Wrapper disabled={disabled ? 1 : 0}>
      <Menu
        data-testid={testId || "accordion"}
        arrowRight={arrowRight ? 1 : 0}
        wrap={wrap ? 1 : 0}
        compact={compact ? 1 : 0}
        onClick={e => {
          if (!disabled) {
            setOpen(show => !show);
            if (handleOpen) handleOpen(e);
          }
        }}>
        {arrowRight ? (
          <>
            {heading}
            {arrow}
          </>
        ) : (
          <>
            {arrow}
            {heading}
          </>
        )}
      </Menu>
      {open && children}
    </Wrapper>
  );
};

Accordion.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  handleOpen: PropTypes.func,
  toggleOpen: PropTypes.bool,
  testId: PropTypes.string,
  disabled: PropTypes.bool,
  isGroup: PropTypes.bool,
  wrap: PropTypes.bool,
  arrowRight: PropTypes.bool,
  compact: PropTypes.bool
};

// Style Overrides
const Wrapper = styled.div`
  width: 100%;

  ${({disabled}) =>
    disabled &&
    css`
      fieldset {
        cursor: not-allowed;
      }
      opacity: 0.2;
    `}
`;

const Ellipsis = styled.span`
  min-width: 0;
`;

const Menu = styled.fieldset`
  ${flex("row", "nowrap", "flex-start", "center")}
  min-width: 0;
  border: ${border} solid transparent;
  border-radius: ${radius};
  overflow: ${({wrap}) => (wrap ? "visible" : "hidden")};
  cursor: pointer;

  ${Label} {
    position: relative;
    width: fit-content;
    min-width: 0;
    overflow: ${({wrap}) => (wrap ? "visible" : "hidden")};
    text-overflow: ellipsis;

    ${({compact}) =>
      compact
        ? css`
            display: inline-flex;
            align-items: center;
            flex: 0 1 auto;
            width: auto;
            max-width: 100%;
            padding: 0;
            margin: 0;
            overflow: hidden;
            white-space: nowrap;
            gap: ${pad / 4}px;

            > * {
              flex-shrink: 0;
            }

            ${Ellipsis}, ${Abbr} {
              flex: 0 1 auto;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          `
        : css`
            display: block;
            padding: ${pad / 2}px;
            margin: ${pad / 2}px;
            padding-left: ${pad}px;

            ${({arrowRight}) =>
              arrowRight
                ? css`
                    padding-right: ${pad / 2}px;
                    margin-right: 0;
                  `
                : css`
                    text-indent: -${pad}px;
                  `}

            ${({isGroup, theme}) =>
              isGroup &&
              css`
                ${voice.medium};
                color: ${theme.group};
                text-transform: uppercase;
                margin-bottom: ${pad / 2}px;
              `}
          `}
  }
`;

const Arrow = styled.span`
  width: fit-content;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: ${({compact}) => (compact ? 0 : `${pad / 2}px`)};
  padding-right: ${({arrowRight, compact}) => {
    if (compact) return arrowRight ? `${pad / 4}px` : 0;
    return arrowRight ? `${pad / 2}px` : 0;
  }};
  padding-left: ${({arrowRight, compact}) => {
    if (compact) return arrowRight ? `${pad / 4}px` : 0;
    return arrowRight ? 0 : `${pad / 2}px`;
  }};
  transform: rotate(0deg);
  transition: ${transition};

  ${({isGroup, theme}) => triangle(pad / 2, isGroup ? theme.group : theme.secondary, "right")}

  ${({openDrawer}) =>
    openDrawer &&
    css`
      transform: rotate(90deg);
    `}
`;

export default Accordion;
